import React, { useEffect, useState } from "react";
import { Table, Button, Form, Row, Col, Modal, Badge, Alert } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";

// Phase 4A, §7/§15/§23. Delegation lets the official assignee hand off
// all/part of the work while accountability stays with them — this manager
// never offers a control that changes tasks.assigned_to.
const DELEGATION_TYPES = ["FULL_WORK", "PARTIAL_WORK", "ASSISTANCE", "SPECIALIST_SUPPORT", "TEMPORARY_COVER"];

const initialForm = { delegated_to_user_id: "", delegation_type: "PARTIAL_WORK", responsibility: "", scope_description: "", percentage: "", starts_at: "", ends_at: "", reason: "" };

function statusVariant(status) {
  switch (status) {
    case "ACTIVE": return "success";
    case "COMPLETED": return "info";
    case "REVOKED": return "danger";
    case "EXPIRED": return "secondary";
    default: return "light";
  }
}

export default function TaskDelegationManager({ taskId, readOnly }) {
  const [delegations, setDelegations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}/delegations`);
      setDelegations(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Load delegations error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/users").then((res) => setUsers(Array.isArray(res.data) ? res.data : [])).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const submitCreate = async () => {
    if (!form.delegated_to_user_id) { toast.warning("Select an employee to delegate to"); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/delegations`, {
        delegated_to_user_id: Number(form.delegated_to_user_id),
        delegation_type: form.delegation_type,
        responsibility: form.responsibility || undefined,
        scope_description: form.scope_description || undefined,
        percentage: form.percentage !== "" ? Number(form.percentage) : undefined,
        starts_at: form.starts_at || undefined,
        ends_at: form.ends_at || undefined,
        reason: form.reason || undefined,
      });
      toast.success("Delegation created");
      setShowAdd(false);
      setForm(initialForm);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_DELEGATION_SELF_NOT_ALLOWED") toast.error("You cannot delegate a task to yourself.");
      else if (data?.code === "TASK_DELEGATION_EXISTS") toast.error("An identical active delegation already exists for this employee.");
      else if (data?.code === "TASK_PROJECT_MEMBER_REQUIRED") toast.error("This employee must be an active member of this task's project/module.");
      else if (data?.code === "TASK_DELEGATION_ACCESS_DENIED") toast.error("Only the task's official assignee can delegate this task.");
      else toast.error(data?.message || "Failed to create delegation");
    } finally {
      setSaving(false);
    }
  };

  const complete = async (d) => {
    try {
      await api.post(`/tasks/${taskId}/delegations/${d.id}/complete`);
      toast.success("Delegation marked complete");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to complete delegation");
    }
  };

  const revoke = async (d) => {
    const reason = window.prompt(`Revoke delegation to ${d.delegated_to_name}? Enter a reason (optional):`);
    if (reason === null) return;
    try {
      await api.post(`/tasks/${taskId}/delegations/${d.id}/revoke`, { revocation_reason: reason || undefined });
      toast.success("Delegation revoked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to revoke delegation");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Delegated To</strong>
        {!readOnly && <Button size="sm" variant="outline-primary" onClick={() => setShowAdd(true)}>+ Delegate</Button>}
      </div>

      <Alert variant="secondary" className="small py-2">Delegation does not change the official task assignee.</Alert>

      {loading ? <div className="text-muted small">Loading...</div> : (
        <Table size="sm" responsive>
          <thead>
            <tr><th>Delegate</th><th>Type</th><th>Responsibility</th><th>%</th><th>Window</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {delegations.map((d) => (
              <tr key={d.id}>
                <td>{d.delegated_to_name}</td>
                <td><Badge bg="light" text="dark">{d.delegation_type.replace("_", " ")}</Badge></td>
                <td className="small">{d.responsibility || d.scope_description || "—"}</td>
                <td className="small">{d.percentage != null ? `${d.percentage}%` : "—"}</td>
                <td className="small">
                  {d.starts_at ? new Date(d.starts_at).toLocaleDateString() : "—"} → {d.ends_at ? new Date(d.ends_at).toLocaleDateString() : "—"}
                </td>
                <td><Badge bg={statusVariant(d.status)}>{d.status}</Badge></td>
                <td>
                  {!readOnly && d.status === "ACTIVE" && (
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-success" onClick={() => complete(d)}>Complete</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => revoke(d)}>Revoke</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!delegations.length && <tr><td colSpan="7" className="text-muted small">No delegations on this task.</td></tr>}
          </tbody>
        </Table>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delegate Task</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small py-2">Delegation does not change the official task assignee.</Alert>
          <Row className="g-2">
            <Col md={12}>
              <Form.Label className="small">Delegate To</Form.Label>
              <Form.Select value={form.delegated_to_user_id} onChange={(e) => setForm({ ...form, delegated_to_user_id: e.target.value })}>
                <option value="">Select...</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Delegation Type</Form.Label>
              <Form.Select value={form.delegation_type} onChange={(e) => setForm({ ...form, delegation_type: e.target.value })}>
                {DELEGATION_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Responsibility (optional)</Form.Label>
              <Form.Control value={form.responsibility} onChange={(e) => setForm({ ...form, responsibility: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label className="small">Scope Description (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.scope_description} onChange={(e) => setForm({ ...form, scope_description: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label className="small">Percentage (optional)</Form.Label>
              <Form.Control type="number" min={0} max={100} value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label className="small">Starts</Form.Label>
              <Form.Control type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label className="small">Ends</Form.Label>
              <Form.Control type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label className="small">Reason (optional)</Form.Label>
              <Form.Control value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitCreate}>{saving ? "Delegating..." : "Delegate"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
